import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { useConnections } from '@/entities/connection';
import { useDatasets } from '@/features/catalog-browser';
import { SCREEN_PATHS } from '@/shared/routes';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { InfoTip } from '@/shared/ui/InfoTip';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';

import { useCreateBinding, useRuleVersions, useRules } from '../api/rules';
import type { Rule, Severity } from '../api/rules';

/* Binding a rule to a dataset, as the design's dialog: find a rule, pick its version, find the table,
 * say how severe a failure is.
 *
 * **Only an active rule can be bound, and the list says why the others cannot.** A draft needs its
 * two signatures and a deprecated rule takes on no new datasets; both are listed, dimmed, with the
 * reason and a way to the rule, so a reader looking for one learns what to do rather than concluding
 * it does not exist.
 *
 * **The target is a name, and the helper says what that means**: a binding matches `schema.table` on
 * every connection, because selectors are name globs. `*` binds the rule to every dataset in the
 * tenant and is offered first, named for what it does.
 *
 * Nothing is chosen for the reader: no rule is pre-selected, and Bind rule stays disabled until a
 * rule, a version and a dataset are all picked — a form that submitted `*` because nobody chose a
 * table would bind a rule to the whole estate by default.
 */

const SEVERITIES: readonly Severity[] = ['blocker', 'error', 'warning', 'info'];
/* How many dataset matches the combobox lists before asking for a narrower search. */
const MAX_DATASET_HITS = 8;
const EVERY_DATASET = '*';

export function BindRuleForm({
  onCreated,
  onCancel,
  initialTarget,
  initialRuleId,
}: {
  readonly onCreated: () => void;
  readonly onCancel: () => void;
  /** A qualified `schema.table` to start from — the dataset whose page opened this dialog. */
  readonly initialTarget?: string | undefined;
  readonly initialRuleId?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const rules = useRules('');
  const datasets = useDatasets({ search: '', connectionId: null });
  const connections = useConnections();
  const createBinding = useCreateBinding();

  const [ruleQuery, setRuleQuery] = useState('');
  const [ruleId, setRuleId] = useState(initialRuleId ?? '');
  const [versionId, setVersionId] = useState('');
  const [target, setTarget] = useState(initialTarget ?? '');
  const [targetQuery, setTargetQuery] = useState('');
  const [targetOpen, setTargetOpen] = useState(false);
  const [columns, setColumns] = useState('');
  const [severity, setSeverity] = useState<Severity>('warning');

  const versions = useRuleVersions(ruleId);
  const versionItems = useMemo(() => versions.data?.items ?? [], [versions.data]);
  /* The newest published version, chosen once the versions arrive — cleared when the rule has none,
     so the dialog cannot submit a version belonging to the previously picked rule. */
  useEffect(() => {
    setVersionId(versionItems[0]?.id ?? '');
  }, [versionItems]);

  const allRules = rules.data?.items ?? [];
  const needle = ruleQuery.trim().toLowerCase();
  const shownRules = allRules
    .filter((rule) => needle === '' || rule.name.toLowerCase().includes(needle))
    /* Active first: they are the ones that can be picked. Stable otherwise. */
    .sort((a, b) => Number(b.state === 'active') - Number(a.state === 'active'));
  const rule = allRules.find((each) => each.id === ruleId);
  const version = versionItems.find((each) => each.id === versionId);

  const names = new Map(
    (connections.data?.items ?? []).map((each) => [each.id, each.name]),
  );
  const qualified = (datasets.data?.items ?? []).map((row) => ({
    id: row.id,
    name: `${row.schema_name}.${row.object_name}`,
    objectType: row.object_type,
    connection: names.get(row.connection_id) ?? '',
  }));
  const dsNeedle = targetQuery.trim().toLowerCase();
  const hits = qualified.filter(
    (row) => dsNeedle === '' || row.name.toLowerCase().includes(dsNeedle),
  );
  const offerEvery =
    dsNeedle === '' || EVERY_DATASET.includes(dsNeedle) || 'all'.includes(dsNeedle);

  /* Only `not_null` and `unique` read columns from the binding, and only when the version names
     none — so the field is shown for those two kinds and sent only when it can do something. */
  const columnsShown = rule?.kind === 'not_null' || rule?.kind === 'unique';
  const columnsApply = columnsShown && (version?.expression.columns.length ?? 0) === 0;

  const ready = rule !== undefined && version !== undefined && target !== '';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!ready) return;
    createBinding.mutate(
      {
        ruleId: rule.id,
        ruleVersionId: version.id,
        selectors: [
          { kind: 'name_glob', description: `Name matches ${target}`, pattern: target },
        ],
        severity,
        explicitColumns: columnsApply
          ? columns
              .split(',')
              .map((each) => each.trim())
              .filter(Boolean)
          : [],
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <form className="lq-bind" onSubmit={submit}>
      <div className="lq-bind__body">
        <div className="lq-bind__field">
          <div className="lq-bind__labelRow">
            <span className="lq-bind__label" id="bind-rule-label">
              {t('rules.bindings.create.ruleLabel')}
            </span>
            <input
              type="search"
              className="lq-input lq-bind__find"
              aria-label={t('rules.bindings.create.findRule')}
              placeholder={t('rules.bindings.create.findRule')}
              value={ruleQuery}
              onChange={(event) => {
                setRuleQuery(event.target.value);
              }}
            />
          </div>
          <div
            className="lq-bind__rules"
            role="radiogroup"
            aria-labelledby="bind-rule-label"
          >
            {shownRules.map((each) => (
              <RuleOption
                key={each.id}
                rule={each}
                picked={each.id === ruleId}
                onPick={() => {
                  setRuleId(each.id);
                }}
              />
            ))}
          </div>
          {rules.isSuccess && shownRules.length === 0 ? (
            <p className="lq-bind__quiet">{t('rules.bindings.create.noRule')}</p>
          ) : null}
        </div>

        {rule === undefined ? null : (
          <label className="lq-bind__field">
            <span className="lq-bind__label">
              {t('rules.bindings.create.versionLabel')}
            </span>
            <select
              className="lq-input"
              value={versionId}
              onChange={(event) => {
                setVersionId(event.target.value);
              }}
            >
              {versionItems.length === 0 ? (
                <option value="">{t('rules.bindings.noVersion')}</option>
              ) : (
                versionItems.map((each) => (
                  <option key={each.id} value={each.id}>
                    v{each.version}
                  </option>
                ))
              )}
            </select>
          </label>
        )}

        <div className="lq-bind__field">
          <span className="lq-bind__label" id="bind-target-label">
            {t('rules.bindings.create.datasetLabel')}
          </span>
          {target !== '' ? (
            <div className="lq-bind__picked">
              <span className="lq-bind__pickedName">
                {target === EVERY_DATASET
                  ? t('rules.bindings.create.everyDataset')
                  : target}
              </span>
              {initialTarget !== undefined && target === initialTarget ? (
                <span className="lq-bind__from">
                  {t('rules.bindings.create.fromDataset')}
                </span>
              ) : null}
              <button
                type="button"
                className="lq-bind__clear"
                aria-label={t('rules.bindings.create.changeDataset')}
                onClick={() => {
                  setTarget('');
                  setTargetQuery('');
                  setTargetOpen(true);
                }}
              >
                ×
              </button>
            </div>
          ) : (
            <>
              <input
                type="search"
                className="lq-input lq-bind__dsInput"
                aria-labelledby="bind-target-label"
                placeholder={t('rules.bindings.create.datasetPlaceholder')}
                value={targetQuery}
                onFocus={() => {
                  setTargetOpen(true);
                }}
                onChange={(event) => {
                  setTargetQuery(event.target.value);
                  setTargetOpen(true);
                }}
              />
              {targetOpen ? (
                <ul
                  className="lq-bind__hits"
                  role="listbox"
                  aria-labelledby="bind-target-label"
                >
                  {offerEvery ? (
                    <li role="option" aria-selected="false">
                      <button
                        type="button"
                        className="lq-bind__hit"
                        onClick={() => {
                          setTarget(EVERY_DATASET);
                          setTargetOpen(false);
                        }}
                      >
                        <span className="lq-bind__hitName">
                          {t('rules.bindings.create.everyDataset')}
                        </span>
                        <span className="lq-bind__hitConn">
                          {t('rules.bindings.create.everyDatasetMeaning')}
                        </span>
                      </button>
                    </li>
                  ) : null}
                  {hits.slice(0, MAX_DATASET_HITS).map((row) => (
                    <li key={row.id} role="option" aria-selected="false">
                      <button
                        type="button"
                        className="lq-bind__hit"
                        onClick={() => {
                          setTarget(row.name);
                          setTargetOpen(false);
                        }}
                      >
                        <span className="lq-bind__hitName">
                          <DatasetObjectTypeIcon objectType={row.objectType} />
                          {row.name}
                        </span>
                        <span className="lq-bind__hitConn">{row.connection}</span>
                      </button>
                    </li>
                  ))}
                  {hits.length > MAX_DATASET_HITS ? (
                    <li className="lq-bind__more">
                      {t('rules.bindings.create.moreDatasets', {
                        count: hits.length - MAX_DATASET_HITS,
                      })}
                    </li>
                  ) : null}
                  {hits.length === 0 && dsNeedle !== '' ? (
                    <li className="lq-bind__more">
                      {t('rules.bindings.create.noDataset')}
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </>
          )}
          <span className="lq-bind__help">{t('rules.bindings.create.datasetHelp')}</span>
        </div>

        {columnsShown ? (
          <label className="lq-bind__field">
            <span className="lq-bind__label">
              {t('rules.bindings.create.columnLabel')}
            </span>
            <input
              className="lq-input"
              value={columns}
              placeholder={t('rules.bindings.create.columnPlaceholder')}
              onChange={(event) => {
                setColumns(event.target.value);
              }}
            />
            <span className="lq-bind__help">{t('rules.bindings.create.columnHelp')}</span>
          </label>
        ) : null}

        <div className="lq-bind__field">
          <span className="lq-bind__label">
            {t('rules.bindings.create.severityLabel')}
            <InfoTip
              subject={t('rules.bindings.create.severityLabel')}
              text={t('help.field.severity')}
            />
          </span>
          <SegmentedFilter
            variant="track"
            label={t('rules.bindings.create.severityLabel')}
            options={SEVERITIES.map((each) => ({
              id: each,
              label: t(`rules.severity.${each}.label`),
            }))}
            value={severity}
            onChange={setSeverity}
          />
        </div>

        {createBinding.isError ? (
          <p className="lq-bind__error" role="alert">
            {createBinding.error.message}
          </p>
        ) : null}
      </div>

      <div className="lq-bind__foot">
        <button type="button" className="lq-btn lq-btn--secondary" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="lq-btn lq-btn--primary"
          disabled={!ready || createBinding.isPending}
        >
          {createBinding.isPending
            ? t('rules.bindings.create.binding')
            : t('rules.bindings.create.submit')}
        </button>
      </div>
    </form>
  );
}

/* One rule as a radio card: its name and kind, its state, and — when it cannot be bound — why, with
 * the way to the rule. */
function RuleOption({
  rule,
  picked,
  onPick,
}: {
  readonly rule: Rule;
  readonly picked: boolean;
  readonly onPick: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const bindable = rule.state === 'active';
  return (
    <div
      className="lq-bind__rule"
      role="radio"
      aria-checked={picked}
      aria-disabled={!bindable}
      tabIndex={bindable ? 0 : -1}
      data-picked={picked ? 'true' : undefined}
      data-disabled={bindable ? undefined : 'true'}
      onClick={() => {
        if (bindable) onPick();
      }}
      onKeyDown={(event) => {
        if (bindable && (event.key === ' ' || event.key === 'Enter')) {
          event.preventDefault();
          onPick();
        }
      }}
    >
      <span className="lq-bind__ruleText">
        <span className="lq-bind__ruleHead">
          <span className="lq-bind__ruleName">{rule.name}</span>
          <span className="lq-bind__ruleKind">{rule.kind}</span>
        </span>
        {bindable ? null : (
          <span className="lq-bind__why">
            {t(`rules.bindings.create.why.${rule.state}`)} ·{' '}
            <Link to={generatePath(SCREEN_PATHS.rule, { ruleId: rule.id })}>
              {t('rules.bindings.create.openRule')}
            </Link>
          </span>
        )}
      </span>
      <span className="lq-bind__state" data-state={rule.state}>
        {t(`rules.state.${rule.state}.label`)}
      </span>
    </div>
  );
}
